/**
 * Color palette from Orchard Design System
 *
 * Organized into:
 * - Primary: Brand colors (Martinique, Persimmon, Sea)
 * - Accent: Supporting colors (Sky, Birch, Peach)
 * - Grays: Neutral scale (100 to 5)
 * - Action States: Hover and disabled states
 * - UI Elements: Semantic colors (Info, Success, Caution, Error)
 * - Sea Accents: Teal accent variations
 */

// =============================================================================
// Primary Colors
// =============================================================================

export const primary = {
  martinique: '#31324C',
  persimmon: '#EB614C',
  sea: '#027F87',
} as const

// =============================================================================
// Accent Colors
// =============================================================================

export const accent = {
  sky: '#A7E9F1',
  skyLight: '#EDFBFD',
  peach: '#F4D9C9',
  darkBirch: '#E0DACD',
  birch: '#F1EEEA',
  lightBirch: '#F9F8F5',
} as const

// =============================================================================
// Gray Scale
// =============================================================================

export const gray = {
  100: '#101234',
  85: '#505167',
  70: '#6F7082',
  50: '#8E8E9D',
  30: '#ADADB7',
  20: '#C5C6CD',
  10: '#DEDEE2',
  5: '#F1F1F2',
} as const

// =============================================================================
// Base Colors
// =============================================================================

export const base = {
  white: '#FFFFFF',
  black: '#000000',
} as const

// =============================================================================
// Action States
// =============================================================================

export const actionStates = {
  // Primary (Persimmon) - for CTAs
  primary: primary.persimmon,
  primaryHover: '#BF391E',
  primaryDisabled: '#F1513B', // 35% opacity

  // Secondary (Sea) - for links and secondary actions
  secondary: primary.sea,
  secondaryHover: '#005A63',
  secondaryDisabled: '#00838C', // 30% opacity
} as const

// =============================================================================
// UI Element Colors (Semantic)
// =============================================================================

export const ui = {
  // Information
  info: '#116ADF',
  infoOutline: '#74AFFF',
  infoBackground: '#EEF3FA',

  // Success
  success: '#1B7D54',
  successOutline: '#36B37E',
  successBackground: '#EDF9F3',

  // Caution (Warning)
  caution: '#A36200',
  cautionOutline: '#DEBE76',
  cautionBackground: '#FFF5EC',

  // Error
  error: '#D70027',
  errorActive: '#B8001F',
  errorOutline: '#E34D68',
  errorBackground: '#FEEFEF',
} as const

// =============================================================================
// Sea Accents
// =============================================================================

export const seaAccent = {
  75: '#2EB0BD',
  50: '#80C1C5',
  25: '#B5DADD',
} as const

// =============================================================================
// Combined Colors Export (for convenience)
// =============================================================================

export const colors = {
  // Primary
  martinique: primary.martinique,
  persimmon: primary.persimmon,
  sea: primary.sea,

  // Accent
  sky: accent.sky,
  skyLight: accent.skyLight,
  peach: accent.peach,
  darkBirch: accent.darkBirch,
  birch: accent.birch,
  lightBirch: accent.lightBirch,

  // Grays
  gray100: gray[100],
  gray85: gray[85],
  gray70: gray[70],
  gray50: gray[50],
  gray30: gray[30],
  gray20: gray[20],
  gray10: gray[10],
  gray5: gray[5],

  // Base
  white: base.white,
  black: base.black,

  // Action States - Primary (Persimmon)
  actionPrimary: actionStates.primary,
  actionPrimaryHover: actionStates.primaryHover,
  actionPrimaryDisabled: actionStates.primaryDisabled,

  // Action States - Secondary (Sea)
  actionSecondary: actionStates.secondary,
  actionSecondaryHover: actionStates.secondaryHover,
  actionSecondaryDisabled: actionStates.secondaryDisabled,

  // UI Elements
  info: ui.info,
  infoOutline: ui.infoOutline,
  infoBackground: ui.infoBackground,
  success: ui.success,
  successOutline: ui.successOutline,
  successBackground: ui.successBackground,
  caution: ui.caution,
  cautionOutline: ui.cautionOutline,
  cautionBackground: ui.cautionBackground,
  error: ui.error,
  errorActive: ui.errorActive,
  errorOutline: ui.errorOutline,
  errorBackground: ui.errorBackground,

  // Sea Accents
  sea75: seaAccent[75],
  sea50: seaAccent[50],
  sea25: seaAccent[25],
} as const
